import { BooleanInput } from '@angular/cdk/coercion';
import {
  Directive,
  InputSignal,
  input,
  inject,
  Signal,
  WritableSignal,
  signal,
  ElementRef,
  viewChild,
  InputSignalWithTransform,
  booleanAttribute
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { InputmaskOptions } from '@ngneat/input-mask';
import { Store } from '@ngrx/store';
import { Subscription, filter, map, switchMap, tap } from 'rxjs';

import { CommonDataApiService } from '@api-services/common-data-api.service';
import { LegalStructureApiService } from '@api-services/legal-structure-api.service';
import { Role } from '@enums/role.enum';
import { CompanyForm } from '@forms/company-form';
import { GiTaskCategoryType } from '@gi/enums/gi-task-category-type.enum';
import { LegalStructureGiTaskCategory } from '@gi/models/legal-structure-gi-task-category';
import { FormHelper } from '@helpers/form.helper';
import { MaskHelper } from '@helpers/mask.helper';
import { AppStoreState } from '@interfaces/app-store-state';
import { LegalStructureAndSignatory } from '@interfaces/legal-structure-and-signatory';
import { ApeCode } from '@models/ape-code';
import { BusinessUnit } from '@models/business-unit';
import { LegalStructure } from '@models/legal-structure';
import { Signatory } from '@models/signatory';
import { businessUserHasRole } from '@modules/core/store/business-user/business-user-selector';

@Directive()
export abstract class CompanyFormBase {
  readonly businessUnits: InputSignal<BusinessUnit[]> = input.required<BusinessUnit[]>();
  readonly legalStructureAndSignatoryDisabled: InputSignalWithTransform<boolean, BooleanInput> = input<
    boolean,
    BooleanInput
  >(false, {
    transform: booleanAttribute
  });
  readonly synchronizeBuWithLegalStructure: InputSignalWithTransform<boolean, BooleanInput> = input<
    boolean,
    BooleanInput
  >(false, {
    transform: booleanAttribute
  });

  readonly companyNameInput: Signal<ElementRef<HTMLInputElement>> =
    viewChild<ElementRef<HTMLInputElement>>('companyNameInput');
  readonly companySiretInput: Signal<ElementRef<HTMLInputElement>> =
    viewChild<ElementRef<HTMLInputElement>>('companySiretInput');

  readonly commonDataApiService: CommonDataApiService = inject<CommonDataApiService>(CommonDataApiService);
  readonly legalStructureApiService: LegalStructureApiService =
    inject<LegalStructureApiService>(LegalStructureApiService);
  readonly store: Store<AppStoreState> = inject<Store<AppStoreState>>(Store<AppStoreState>);

  readonly legalForms: Signal<string[]> = toSignal(this.commonDataApiService.getLegalForms(), {
    rejectErrors: true
  });
  readonly apeCodes: Signal<ApeCode[]> = toSignal(this.commonDataApiService.getApeCodes(), {
    rejectErrors: true
  });

  readonly legalStructuresAndSignatories: WritableSignal<LegalStructureAndSignatory[]> = signal<
    LegalStructureAndSignatory[]
  >([]);

  readonly siretMask: InputmaskOptions<null> = MaskHelper.getSiretAlphaNumericMask();

  companyForm: CompanyForm;

  resetLegalStructureAndSignatorySubscription: Subscription | null = null;
  legalStructureAndSignatorySubscription: Subscription | null = null;

  focusCompanyNameInput(): void {
    setTimeout(() => this.companyNameInput()?.nativeElement?.focus());
  }

  focusCompanySiretInput(): void {
    setTimeout(() => this.companySiretInput()?.nativeElement?.focus());
  }

  resetLegalStructureAndSignatoryWhenBUChange(): void {
    if (this.resetLegalStructureAndSignatorySubscription) {
      this.resetLegalStructureAndSignatorySubscription.unsubscribe();
    }

    this.resetLegalStructureAndSignatorySubscription = this.companyForm.businessUnit.valueChanges
      .pipe(tap(() => this.companyForm.legalStructureAndSignatory.reset()))
      .subscribe();
  }

  setLegalStructureAndSignatory(): void {
    if (this.legalStructureAndSignatorySubscription) {
      this.legalStructureAndSignatorySubscription.unsubscribe();
    }

    this.legalStructureAndSignatorySubscription = this.store
      // TODO: À remplacer par une vérification sur les permissions/ACL lorsque la modification back sera faite
      .select(businessUserHasRole([Role.PARTNER_ACCOUNTANT, Role.ENGINE_ADMIN, Role.PARTNER_ADMIN]))
      .pipe(
        filter((hasRole: boolean) => hasRole),
        switchMap(() => FormHelper.getValueChange(this.companyForm.businessUnit)),
        tap(() => {
          this.legalStructuresAndSignatories.set([]);
          this.companyForm.legalStructureAndSignatory.disable();
        }),
        filter((businessUnit: BusinessUnit) => !!businessUnit?.id),
        switchMap((businessUnit: BusinessUnit) =>
          this.legalStructureApiService.getBusinessUnitLegalStructures(businessUnit.id, null, true)
        ),
        map((legalStructures: LegalStructure[]) => this.filteredLegalStructures(legalStructures)),
        filter((legalStructures: LegalStructure[]) => !!legalStructures?.length),
        tap((legalStructuresAndSignatories: LegalStructureAndSignatory[]) => {
          this.legalStructuresAndSignatories.set(legalStructuresAndSignatories);
          this.setDefaultLegalStructureAndSignatory(legalStructuresAndSignatories);
          this.enableLegalStructureAndSignatory();
        })
      )
      .subscribe();
  }

  private enableLegalStructureAndSignatory(): void {
    if (this.legalStructureAndSignatoryDisabled() || this.companyForm.legalStructureAndSignatory.enabled) {
      return;
    }

    this.companyForm.legalStructureAndSignatory.enable();
  }

  private setDefaultLegalStructureAndSignatory(legalStructures: LegalStructure[]): void {
    if (!this.synchronizeBuWithLegalStructure()) {
      return;
    }

    const defaultLegalStructureAndSignatory: LegalStructureAndSignatory | undefined = legalStructures.find(
      (legalStructureAndSignatory: LegalStructureAndSignatory) =>
        legalStructureAndSignatory.legalStructure.id ===
          this.companyForm.businessUnit.value.defaultCompanyLegalStructure?.id &&
        legalStructureAndSignatory.signatory.id === this.companyForm.businessUnit.value.defaultCompanySignatory.id
    );

    this.companyForm.legalStructureAndSignatory.setValue(defaultLegalStructureAndSignatory, { emitEvent: false });
  }

  private filteredLegalStructures(legalStructures: LegalStructure[]): LegalStructure[] {
    return legalStructures
      .filter((legalStructure: LegalStructure) => {
        const unarchivedSignatoriesCount: number | undefined = legalStructure.signatories.filter(
          (signatory: Signatory) => !signatory.archived
        ).length;
        const hasAccountingCategory: boolean = legalStructure.giTaskCategories.some(
          (category: LegalStructureGiTaskCategory) => category.giTaskCategory.type === GiTaskCategoryType.accounting
        );
        return !legalStructure.archived && unarchivedSignatoriesCount > 0 && hasAccountingCategory;
      })
      .reduce(
        (acc: LegalStructureAndSignatory[], legalStructure: LegalStructure) => [
          ...acc,
          ...LegalStructure.toLegalStructureAndSignatories(legalStructure)
        ],
        [] as LegalStructureAndSignatory[]
      );
  }
}
